import { Loader2 } from "lucide-react";
import { FC, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { ChronologyEvent } from "~/api/model";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "~/components/ui/alert-dialog";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { ScrollArea } from "~/components/ui/scroll-area";
import { useToast } from "~/hooks/use-toast";
import { trackEvent } from "~/utils/analytics";
import { formatChronologyEventDate } from "../helpers/chronologyRequest";
import {
  ChronologyDuplicateGroup,
  ChronologyDuplicateTier,
} from "../helpers/detectDuplicateEvents";
import { useChronologyMutations } from "../hooks/useChronology";
import { ChronologyDuplicateEvent } from "./ChronologyDuplicateEvent";

interface ChronologyDuplicatesModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * The single group under review, or null when no row's dot is selected. The
   * parent recomputes it as events change, so it shrinks as members are deleted
   * and becomes null once the group drops below two members.
   */
  group: ChronologyDuplicateGroup | null;
}

const TIER_CONFIG: Record<
  ChronologyDuplicateTier,
  { variant: "default" | "secondary"; labelId: string }
> = {
  exact: { variant: "default", labelId: "chronology.duplicates.tier.exact" },
  similar: {
    variant: "secondary",
    labelId: "chronology.duplicates.tier.similar",
  },
};

/**
 * Surfaces one fuzzy-matched group of likely-duplicate chronology events for
 * manual review, opened from the colored dot on a flagged row. Detection is
 * advisory: nothing is removed automatically, and the only mutation available
 * is an explicit, per-event delete. The group is passed in from the parent and
 * recomputes as the underlying events change, so it closes once it drops below
 * two members.
 */
export const ChronologyDuplicatesModal: FC<ChronologyDuplicatesModalProps> = ({
  open,
  onOpenChange,
  group,
}) => {
  const intl = useIntl();
  const { toast } = useToast();
  const { remove } = useChronologyMutations();
  const [eventToDelete, setEventToDelete] = useState<ChronologyEvent | null>(
    null
  );
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const isDeleting = deletingId !== null;

  // Context for the delete confirmation. Blank-detail rows lean on metadata, and
  // source (page/Bates) keeps otherwise identical same-day rows distinguishable.
  const deleteMetadata = eventToDelete
    ? [
        eventToDelete.provider,
        eventToDelete.facility,
        eventToDelete.documentCategory,
      ]
        .map((value) => value.trim())
        .filter((value) => value.length > 0)
    : [];
  const deleteSource = (() => {
    if (!eventToDelete) return null;
    const { pageNumber, batesNumber } = eventToDelete.reference;
    if (batesNumber) return batesNumber;
    if (pageNumber != null) {
      return intl.formatMessage(
        { id: "chronology.source.page" },
        { page: pageNumber + 1 }
      );
    }
    return null;
  })();

  const handleConfirmDelete = async () => {
    if (!eventToDelete) return;
    const targetId = eventToDelete.id;
    setDeletingId(targetId);
    try {
      await remove(targetId);
      trackEvent({ eventName: "app/chronology/duplicates_modal.delete" });
      setEventToDelete(null);
    } catch (error) {
      toast({
        variant: "destructive",
        title: intl.formatMessage({ id: "chronology.error.delete" }),
        description:
          error instanceof Error
            ? error.message
            : intl.formatMessage({ id: "chronology.error.unknown" }),
      });
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="flex max-h-[calc(100vh-4rem)] flex-col sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              <FormattedMessage
                id="chronology.duplicates.title"
                defaultMessage="Possible Duplicates"
              />
            </DialogTitle>
            <DialogDescription>
              <FormattedMessage
                id="chronology.duplicates.description"
                defaultMessage="We grouped events that look like duplicates so you can review and tidy them up. Nothing is removed automatically."
              />
            </DialogDescription>
          </DialogHeader>

          {group && (
            <>
              <div className="flex items-center gap-2">
                <Badge
                  variant={TIER_CONFIG[group.tier].variant}
                  className="h-5 text-[10px]"
                >
                  <FormattedMessage id={TIER_CONFIG[group.tier].labelId} />
                </Badge>
                <span className="text-xs text-muted-foreground">
                  <FormattedMessage
                    id="chronology.duplicates.eventCount"
                    defaultMessage="{count, plural, one {# event} other {# events}}"
                    values={{ count: group.events.length }}
                  />
                </span>
              </div>

              <ScrollArea className="-mx-2 min-h-0 flex-1">
                <div className="flex flex-col gap-2 px-2 py-1">
                  {group.events.map((event) => (
                    <ChronologyDuplicateEvent
                      key={event.id}
                      event={event}
                      onDelete={setEventToDelete}
                      isDeleting={deletingId === event.id}
                      disabled={isDeleting}
                    />
                  ))}
                </div>
              </ScrollArea>
            </>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              <FormattedMessage id="close" defaultMessage="Close" />
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={eventToDelete !== null}
        onOpenChange={(nextOpen) => {
          if (!nextOpen && !isDeleting) setEventToDelete(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              <FormattedMessage
                id="chronology.delete.title"
                defaultMessage="Delete Event"
              />
            </AlertDialogTitle>
            <AlertDialogDescription>
              <FormattedMessage
                id="chronology.delete.description"
                defaultMessage="This event will be removed from the chronology. This action cannot be undone."
              />
            </AlertDialogDescription>
          </AlertDialogHeader>
          {eventToDelete && (
            <div className="rounded-md border border-border bg-muted/40 p-3 text-sm">
              <p className="font-medium">
                {formatChronologyEventDate(eventToDelete) ?? (
                  <span className="text-muted-foreground">
                    <FormattedMessage id="chronology.list.undatedLabel" />
                  </span>
                )}
              </p>
              {eventToDelete.eventType.trim().length > 0 && (
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  {eventToDelete.eventType}
                </p>
              )}
              {eventToDelete.detail.trim().length > 0 && (
                <p className="text-muted-foreground">{eventToDelete.detail}</p>
              )}
              {deleteMetadata.length > 0 && (
                <p className="text-xs text-muted-foreground">
                  {deleteMetadata.join(" · ")}
                </p>
              )}
              {deleteSource && (
                <p className="text-xs text-muted-foreground">{deleteSource}</p>
              )}
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>
              <FormattedMessage id="common.cancel" defaultMessage="Cancel" />
            </AlertDialogCancel>
            <Button
              variant="destructive"
              disabled={isDeleting}
              onClick={handleConfirmDelete}
            >
              {isDeleting && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
              <FormattedMessage
                id="chronology.action.delete"
                defaultMessage="Delete"
              />
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
